import { type RefObject, useCallback, useMemo, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import ErrorEst from '~/components/ErrorEst/ErrorEst';
import ListSelect from '~/components/widgets/ListSelect/ListSelect';
import { Basis } from '~/models/studio';
import { useContextMenu } from '~/systems/contextMenu';
import { useCurrentFrame } from '~/systems/currentFrame';
import { useExperiment } from '~/systems/experiment';
import {
  pickStateNames,
  printMeasureIndices,
  printQutritIndices,
  useUiState
} from '~/systems/uiState';
import { assert, assertNever, clamp } from '~/util';
import { useDOMNodeSize } from '~/util/hooks';
import type {
  MeasuredComponent,
  SimulatedComponent
} from '~/systems/simulation';
import type { Vec2 } from '~/util';
import './Histogram.css';

interface HistogramProps {
  headerNode: HTMLElement | null;
}

/** X-Axis is frequency and Y-Axis is qurit state */
const NUM_SAMPLES = 100;
/** Maximum Probability is 1. Thus maximum possible value is 1 * NUM_SAMPLES */
const MAX_FREQ_VAL = 1 * NUM_SAMPLES;
/** Probability threshold */
const MIN_PROBABILITY = 0.005;

const HORIZONTAL_GRAPH_CONF = {
  Y_LABEL_SEP: 20,
  X_LABEL_SEP: -1,
  OFFSET: {
    bottom: { min: 35, max: Infinity },
    left: { min: 20, max: Infinity }
  },
  PADDING: { x: 10, y: 10 },
  X_STEP: 20,
  Y_STEP: 20,
  X_BOUND: { min: 0, max: MAX_FREQ_VAL + 10 },
  Y_BOUND: { min: 0, max: Infinity },
  X_SCALE_TO_FIT: true,
  Y_SCALE_TO_FIT: true,
  X_DISPLAY_GRIDLINES: true,
  Y_DISPLAY_GRIDLINES: false,
  X_AXIS_LABEL: 'Probability [%]',
  Y_AXIS_LABEL: ''
};

const VERTICAL_GRAPH_CONF = {
  Y_LABEL_SEP: -1,
  X_LABEL_SEP: 20,
  OFFSET: {
    bottom: { min: 20, max: Infinity },
    left: { min: 30, max: Infinity }
  },
  PADDING: { x: 10, y: 10 },
  X_STEP: 20,
  Y_STEP: 20,
  X_BOUND: { min: 0, max: Infinity },
  Y_BOUND: { min: 0, max: MAX_FREQ_VAL + 10 },
  X_SCALE_TO_FIT: true,
  Y_SCALE_TO_FIT: true,
  X_DISPLAY_GRIDLINES: false,
  Y_DISPLAY_GRIDLINES: true,
  X_AXIS_LABEL: '',
  Y_AXIS_LABEL: 'Probability [%]'
};

const LINES_CONF = {
  GRID_LINE_STROKE_WIDTH: 1,
  AXIS_LINE_STROKE_WIDTH: 0
};

const HISTOGRAM_CONF = {
  BIN_WIDTH: 20
};

const measureOptions = [
  { label: 'Full state', value: null as Basis | null },
  { label: 'Digital measurement', value: Basis.Digital as Basis | null },
  {
    label: 'Ground-rydberg measurement',
    value: Basis.GroundRydberg as Basis | null
  },
  { label: 'XY measurement', value: Basis.XY as Basis | null }
];

export default function Histogram({ headerNode }: HistogramProps) {
  const currentFrame = useCurrentFrame();
  const { commitedBasis: basis, openTransaction } = useExperiment();
  const uiState = useUiState();
  const menu = useContextMenu();

  const viewportRef = useRef<HTMLDivElement>(null);
  const viewportSize = useDOMNodeSize(viewportRef);
  const stateLabelRef = useRef<SVGGElement>(null);
  // SVGGElement doesn't work directly with useDOMNodeSize (which expects HTMLElement),
  // so we track it manually
  const [stateLabelSize, setStateLabelSize] = useState({ width: 0, height: 0 });

  const [tryShowZeroStates, setTryShowZeroStates] = useState(false);
  const [isVertical, setIsVertical] = useState(true);

  const graphConf = isVertical ? VERTICAL_GRAPH_CONF : HORIZONTAL_GRAPH_CONF;

  const canShowZeroStates = useMemo(() => {
    const maxAtoms = basis === null ? 6 : 9;
    return currentFrame.numAtoms <= maxAtoms;
  }, [basis, currentFrame.numAtoms]);

  const data = useMemo((): (SimulatedComponent | MeasuredComponent)[] => {
    if (tryShowZeroStates && canShowZeroStates) {
      const numAtoms = currentFrame.numAtoms;
      const expandedComponents: SimulatedComponent[] = [];
      const expandedMeasures: MeasuredComponent[] = [];

      const expandQubitZeros = (fromIndex: number, toIndex: number) => {
        for (let i = fromIndex; i < toIndex; i++) {
          expandedMeasures.push({ qubitIndices: i, mag2: 0 });
        }
      };

      const expandQutritZeros = (fromIndex: bigint, toIndex: bigint) => {
        if (fromIndex >= toIndex) {
          return;
        }
        const pushIndex = (qutritIndices: bigint) =>
          expandedComponents.push({ qutritIndices, im: 0, re: 0, mag2: 0 });

        const startMod = fromIndex & 3n;
        if (startMod === 1n) {
          pushIndex(fromIndex++);
          if (fromIndex >= toIndex) {
            return;
          }
        }
        if (startMod === 1n || startMod === 2n) {
          pushIndex(fromIndex++);
          if (fromIndex >= toIndex) {
            return;
          }
        }
        if (startMod === 1n || startMod === 2n || startMod === 3n) {
          fromIndex++;
          if (fromIndex >= toIndex) {
            return;
          }
        }

        for (;;) {
          let invalidMask = fromIndex & (fromIndex >> 1n) & 0x5555555555555555n;
          while (invalidMask) {
            fromIndex += invalidMask;
            invalidMask = fromIndex & (fromIndex >> 1n) & 0x5555555555555555n;
          }
          if (fromIndex + 2n >= toIndex) {
            break;
          }
          pushIndex(fromIndex);
          pushIndex(fromIndex + 1n);
          pushIndex(fromIndex + 2n);
          fromIndex += 4n;
        }

        for (; fromIndex < toIndex; fromIndex++) {
          pushIndex(fromIndex);
        }
      };

      if (basis === null) {
        let startIndex = 0n;
        const componentData = currentFrame.components;
        for (
          let componentIdx = 0;
          componentIdx < componentData.length;
          componentIdx++
        ) {
          const c = componentData[componentIdx];
          expandQutritZeros(startIndex, c.qutritIndices);
          startIndex = c.qutritIndices + 1n;
          expandedComponents.push(c);
        }

        let finalIndex = 0n;
        for (let i = 0; i < numAtoms; i++) {
          finalIndex = (finalIndex << 2n) | 3n;
        }
        expandQutritZeros(startIndex, finalIndex + 1n);
        assert(expandedComponents.length === Math.pow(3, numAtoms));
        return expandedComponents;
      } else {
        let startIndex = 0;
        const measureData = currentFrame.getMeasure(basis);
        for (
          let componentIdx = 0;
          componentIdx < measureData.length;
          componentIdx++
        ) {
          const m = measureData[componentIdx];
          expandQubitZeros(startIndex, m.qubitIndices);
          startIndex = m.qubitIndices + 1;
          expandedMeasures.push(m);
        }
        expandQubitZeros(startIndex, 1 << numAtoms);
        assert(expandedMeasures.length === 1 << numAtoms);
        return expandedMeasures;
      }
    } else {
      if (basis === null) {
        return currentFrame.components.filter((c) => c.mag2 > MIN_PROBABILITY);
      } else {
        return currentFrame
          .getMeasure(basis)
          .filter((c) => c.mag2 > MIN_PROBABILITY);
      }
    }
  }, [tryShowZeroStates, canShowZeroStates, currentFrame, basis]);

  const offset = useMemo(() => {
    const left = graphConf.OFFSET.left.min;
    const bottom = graphConf.OFFSET.bottom.min;
    const labelLength = (currentFrame.numAtoms + 2) * 7;
    return isVertical
      ? {
          left,
          bottom: clamp(
            labelLength + graphConf.PADDING.y,
            graphConf.OFFSET.bottom.min,
            graphConf.OFFSET.bottom.max
          )
        }
      : {
          left: clamp(
            labelLength + graphConf.PADDING.x,
            graphConf.OFFSET.left.min,
            graphConf.OFFSET.left.max
          ),
          bottom
        };
  }, [
    currentFrame.numAtoms,
    graphConf.OFFSET.bottom.max,
    graphConf.OFFSET.bottom.min,
    graphConf.OFFSET.left.max,
    graphConf.OFFSET.left.min,
    graphConf.PADDING.x,
    graphConf.PADDING.y,
    isVertical
  ]);

  const graphSize = useMemo(() => {
    const { width: viewPortWidth, height: viewPortHeight } = viewportSize;
    let width = 0;
    let height = 0;

    if (!graphConf.Y_SCALE_TO_FIT) {
      height = offset.bottom + stateLabelSize.height + graphConf.Y_LABEL_SEP;
    }
    if (!graphConf.X_SCALE_TO_FIT) {
      width = offset.left + stateLabelSize.width + graphConf.X_LABEL_SEP;
    }

    return {
      width: Math.max(viewPortWidth, width),
      height: Math.max(viewPortHeight, height)
    };
  }, [graphConf, offset, stateLabelSize, viewportSize]);

  const plotSize = useMemo(
    () => ({
      width: Math.max(graphSize.width - offset.left, 0),
      height: Math.max(graphSize.height - offset.bottom, 0)
    }),
    [graphSize, offset]
  );

  const graphToViewportMetricMultiplier = useMemo(() => {
    const multiplier: Vec2 = { x: 1, y: 1 };
    if (graphConf.X_SCALE_TO_FIT) {
      multiplier.x =
        Number.isFinite(graphConf.X_BOUND.max) &&
        Number.isFinite(graphConf.X_BOUND.min)
          ? plotSize.width / (graphConf.X_BOUND.max - graphConf.X_BOUND.min)
          : clamp(
              plotSize.width / ((2 * data.length + 1) * graphConf.X_STEP),
              0,
              1.2
            );
    }
    if (graphConf.Y_SCALE_TO_FIT) {
      multiplier.y =
        Number.isFinite(graphConf.Y_BOUND.max) &&
        Number.isFinite(graphConf.Y_BOUND.min)
          ? plotSize.height / (graphConf.Y_BOUND.max - graphConf.Y_BOUND.min)
          : clamp(
              plotSize.height / ((2 * data.length + 1) * graphConf.Y_STEP),
              0,
              1.2
            );
    }
    return multiplier;
  }, [graphConf, plotSize, data]);

  const graphToViewportCoordinateX = useCallback(
    (x: number) =>
      (x - graphConf.X_BOUND.min) * graphToViewportMetricMultiplier.x +
      offset.left,
    [graphConf.X_BOUND.min, graphToViewportMetricMultiplier.x, offset.left]
  );

  const graphToViewportCoordinateY = useCallback(
    (y: number) =>
      plotSize.height -
      (y - graphConf.Y_BOUND.min) * graphToViewportMetricMultiplier.y,
    [graphConf.Y_BOUND.min, graphToViewportMetricMultiplier.y, plotSize.height]
  );

  const graphToViewportCoordinates = useCallback(
    (v: Vec2): Vec2 => ({
      x: graphToViewportCoordinateX(v.x),
      y: graphToViewportCoordinateY(v.y)
    }),
    [graphToViewportCoordinateX, graphToViewportCoordinateY]
  );

  const origin = graphToViewportCoordinates({ x: 0, y: 0 });

  const gridLines = useMemo(() => {
    const xGridLines: { from: Vec2; to: Vec2; label: number }[] = [];
    const yGridLines: { from: Vec2; to: Vec2; label: number }[] = [];
    const displayXGridLines =
      graphConf.X_DISPLAY_GRIDLINES &&
      Number.isFinite(graphConf.X_BOUND.min) &&
      Number.isFinite(graphConf.X_BOUND.max);
    const displayYGridLines =
      graphConf.Y_DISPLAY_GRIDLINES &&
      Number.isFinite(graphConf.Y_BOUND.min) &&
      Number.isFinite(graphConf.Y_BOUND.max);

    for (
      let i =
        Math.ceil(graphConf.X_BOUND.min / graphConf.X_STEP) * graphConf.X_STEP;
      displayXGridLines && i <= graphConf.X_BOUND.max;
      i += graphConf.X_STEP
    ) {
      if (i === 0) {
        continue;
      }
      xGridLines.push({
        from: graphToViewportCoordinates({ x: i, y: 0 }),
        to: { x: graphToViewportCoordinateX(i), y: 0 },
        label: i
      });
    }

    for (
      let i =
        Math.ceil(graphConf.Y_BOUND.min / graphConf.Y_STEP) * graphConf.Y_STEP;
      displayYGridLines && i <= graphConf.Y_BOUND.max;
      i += graphConf.Y_STEP
    ) {
      if (i === 0) {
        continue;
      }
      yGridLines.push({
        from: graphToViewportCoordinates({ x: 0, y: i }),
        to: { x: origin.x + plotSize.width, y: graphToViewportCoordinateY(i) },
        label: i
      });
    }

    return { x: xGridLines, y: yGridLines };
  }, [
    graphConf.X_DISPLAY_GRIDLINES,
    graphConf.X_BOUND.min,
    graphConf.X_BOUND.max,
    graphConf.Y_DISPLAY_GRIDLINES,
    graphConf.Y_BOUND.min,
    graphConf.Y_BOUND.max,
    graphConf.X_STEP,
    graphConf.Y_STEP,
    graphToViewportCoordinates,
    graphToViewportCoordinateX,
    origin.x,
    plotSize.width,
    graphToViewportCoordinateY
  ]);

  const axisLines = useMemo(
    () => ({
      origin,
      yAxis: {
        from: {
          x: origin.x,
          y: graphToViewportCoordinateY(graphConf.Y_BOUND.min)
        },
        to: { x: origin.x, y: 0 }
      },
      xAxis: {
        from: { x: offset.left, y: origin.y },
        to: { x: graphSize.width, y: origin.y }
      }
    }),
    [
      origin,
      graphToViewportCoordinateY,
      graphConf.Y_BOUND.min,
      offset.left,
      graphSize.width
    ]
  );

  const stateLabels = useMemo(() => {
    const numAtoms = currentFrame.numAtoms;
    const config = graphConf;

    const labels: {
      label: string;
      x: number;
      y: number;
      component: SimulatedComponent;
    }[] = [];
    const datapoints = data;
    const isMeasured = datapoints.length > 0 && 'qubitIndices' in datapoints[0];
    let getLabel: (c: SimulatedComponent | MeasuredComponent) => string;
    if (isMeasured) {
      getLabel = (c) =>
        printMeasureIndices((c as MeasuredComponent).qubitIndices, numAtoms);
    } else {
      const stateNames = pickStateNames(uiState.basisRepr);
      getLabel = (c) =>
        printQutritIndices(
          (c as SimulatedComponent).qutritIndices,
          numAtoms,
          stateNames
        );
    }

    if (isVertical) {
      const labelStep = Math.ceil(
        (0.02 / graphToViewportMetricMultiplier.x) * config.X_LABEL_SEP
      );
      for (let i = 0; i < datapoints.length; i += labelStep) {
        const datapoint = datapoints[i];
        labels.push({
          label: getLabel(datapoint),
          x: graphToViewportCoordinateX((2 * i + 0.5) * config.X_LABEL_SEP),
          y: graphSize.height - offset.bottom / 2,
          component: datapoint as SimulatedComponent
        });
      }
    } else {
      const labelStep = Math.ceil(
        (0.02 / graphToViewportMetricMultiplier.y) * config.Y_LABEL_SEP
      );
      for (let i = 0; i < datapoints.length; i += labelStep) {
        const datapoint = datapoints[i];
        labels.push({
          label: getLabel(datapoint),
          x: offset.left / 2,
          y: graphToViewportCoordinateY((2 * i + 1) * config.Y_LABEL_SEP),
          component: datapoint as SimulatedComponent
        });
      }
    }
    return labels;
  }, [
    currentFrame.numAtoms,
    graphConf,
    data,
    isVertical,
    uiState.basisRepr,
    graphToViewportMetricMultiplier.x,
    graphToViewportMetricMultiplier.y,
    graphToViewportCoordinateX,
    graphSize.height,
    offset.bottom,
    offset.left,
    graphToViewportCoordinateY
  ]);

  const histogramBins = useMemo(() => {
    if (isVertical) {
      const binWidth =
        HISTOGRAM_CONF.BIN_WIDTH * graphToViewportMetricMultiplier.x;
      const binShift = -binWidth / 2;
      return data.map((component, i) => {
        const val = graphToViewportCoordinates({
          x: (2 * i + 1) * graphConf.X_STEP,
          y: component.mag2 * NUM_SAMPLES
        });
        return {
          x: val.x + binShift,
          y: val.y,
          height: origin.y - val.y,
          width: binWidth,
          component: component as SimulatedComponent
        };
      });
    } else {
      const binWidth =
        HISTOGRAM_CONF.BIN_WIDTH * graphToViewportMetricMultiplier.y;
      const binShift = -binWidth / 2;
      return data.map((component, i) => {
        const val = graphToViewportCoordinates({
          x: component.mag2 * NUM_SAMPLES,
          y: (2 * i + 1) * graphConf.Y_STEP
        });
        const width = val.x - origin.x;
        return {
          x: origin.x,
          y: val.y + binShift,
          height: HISTOGRAM_CONF.BIN_WIDTH * graphToViewportMetricMultiplier.y,
          width,
          component: component as SimulatedComponent
        };
      });
    }
  }, [
    isVertical,
    graphToViewportMetricMultiplier.x,
    graphToViewportMetricMultiplier.y,
    data,
    graphToViewportCoordinates,
    graphConf.X_STEP,
    graphConf.Y_STEP,
    origin.y,
    origin.x
  ]);

  function flipHistogram() {
    setIsVertical((prev) => !prev);
  }

  function toggleDisplayAll() {
    setTryShowZeroStates((prev) => !prev);
  }

  function contextmenu() {
    menu.registerOnce({ label: 'Flip', action: flipHistogram });
    if (canShowZeroStates) {
      menu.registerOnce({
        label: tryShowZeroStates ? 'Hide zero states' : 'Show zero states',
        action: toggleDisplayAll
      });
    }
  }

  function componentHover(state: SimulatedComponent | null) {
    uiState.setHoveredState(state);
  }

  const measurementState = basis;

  function setMeasurementState(basis: Basis | null) {
    const tx = openTransaction('Set measurement basis');
    tx.push({ kind: 'set_basis', basis });
    tx.commit();
  }

  const basisDescription = useMemo(() => {
    const basis = measurementState;
    switch (basis) {
      case null: {
        const names = pickStateNames(uiState.basisRepr);
        return `❘g⟩ → ${names[0]}, ❘h⟩ → ${names[1]}, ❘r⟩ → ${names[2]}`;
      }
      case Basis.Digital:
        return '❘g⟩ & ❘r⟩ → 0, ❘h⟩ → 1';
      case Basis.GroundRydberg:
        return '❘g⟩ & ❘h⟩ → 0, ❘r⟩ → 1';
      case Basis.XY:
        return '❘0⟩ → 0, ❘1⟩ → 1';
      default:
        return assertNever(basis);
    }
  }, [measurementState, uiState.basisRepr]);

  // Track stateLabelNode size manually
  const stateLabelCallbackRef = useCallback((node: SVGGElement | null) => {
    if (node) {
      const rect = node.getBoundingClientRect();
      setStateLabelSize({ width: rect.width, height: rect.height });
    }
    (stateLabelRef as RefObject<SVGGElement | null>).current = node;
  }, []); // TODO: Improve. Without useMemo, it's maximum callstack exceeded

  return (
    <>
      {headerNode && createPortal(<ErrorEst />, headerNode)}
      <div className="Histogram">
        <div
          className="Histogram-body u-scrollbarDark"
          onContextMenu={contextmenu}
        >
          <div
            ref={viewportRef}
            className="Histogram-viewportSizer u-scrollbarDark"
          >
            <svg
              className="Histogram-plot"
              height={graphSize.height}
              width={graphSize.width}
              aria-labelledby="Measurement Histogram"
            >
              <g>
                <rect
                  className="Histogram-plotGraphBackground"
                  x={offset.left}
                  width={plotSize.width}
                  height={plotSize.height}
                />
              </g>
              <g>
                {gridLines.x.map((line, idx) => (
                  <g key={idx}>
                    <line
                      x1={line.from.x}
                      y1={line.from.y}
                      x2={line.to.x}
                      y2={line.to.y}
                      className="Histogram-gridLine"
                      strokeWidth={LINES_CONF.GRID_LINE_STROKE_WIDTH}
                      fill="black"
                    />
                    <text
                      x={line.from.x}
                      y={line.from.y}
                      dominantBaseline="text-before-edge"
                      textAnchor="middle"
                      className="Histogram-gridlineText"
                    >
                      {line.label}
                    </text>
                  </g>
                ))}
                {gridLines.y.map((line, idx) => (
                  <g key={idx}>
                    <line
                      x1={line.from.x}
                      y1={line.from.y}
                      x2={line.to.x}
                      y2={line.to.y}
                      className="Histogram-gridLine"
                      strokeWidth={LINES_CONF.GRID_LINE_STROKE_WIDTH}
                      fill="black"
                    />
                    <text
                      x={line.from.x}
                      y={line.from.y}
                      dominantBaseline="central"
                      textAnchor="end"
                      className="Histogram-gridlineText"
                    >
                      {line.label}
                    </text>
                  </g>
                ))}
              </g>
              <g>
                <line
                  x1={axisLines.yAxis.from.x}
                  y1={axisLines.yAxis.from.y}
                  x2={axisLines.yAxis.to.x}
                  y2={axisLines.yAxis.to.y}
                  strokeWidth={LINES_CONF.AXIS_LINE_STROKE_WIDTH}
                  stroke="black"
                />
                <line
                  x1={axisLines.xAxis.from.x}
                  y1={axisLines.xAxis.from.y}
                  x2={axisLines.xAxis.to.x}
                  y2={axisLines.xAxis.to.y}
                  strokeWidth={LINES_CONF.AXIS_LINE_STROKE_WIDTH}
                  stroke="black"
                />
                <text
                  x={axisLines.origin.x}
                  y={axisLines.origin.y}
                  dominantBaseline="text-before-edge"
                  textAnchor="middle"
                  className="Histogram-axislineText"
                >
                  0
                </text>
              </g>
              <g className="Histogram-bar">
                {histogramBins.map((bin, i) => (
                  <rect
                    key={i}
                    width={bin.width}
                    height={bin.height}
                    y={bin.y}
                    x={bin.x}
                    onMouseEnter={() => componentHover(bin.component)}
                    onMouseLeave={() => componentHover(null)}
                  />
                ))}
              </g>
              <g ref={stateLabelCallbackRef} className="Histogram-stateLabels">
                {stateLabels.map((label, i) => (
                  <text
                    key={i}
                    className="Histogram-stateLabel"
                    dominantBaseline="central"
                    x={label.x}
                    y={label.y}
                    textAnchor="middle"
                    transform={
                      isVertical ? `rotate(-75 ${label.x} ${label.y})` : ''
                    }
                    onMouseOver={() => componentHover(label.component)}
                    onMouseLeave={() => componentHover(null)}
                  >
                    {label.label}
                  </text>
                ))}
              </g>
              <g className="Histogram-axisLabel">
                <text
                  x={offset.left + plotSize.width / 2}
                  y={graphSize.height - 5}
                  textAnchor="middle"
                  dominantBaseline="text-after-edge"
                >
                  {graphConf.X_AXIS_LABEL}
                </text>
                <text
                  x={0}
                  y={plotSize.height / 2}
                  dominantBaseline="text-before-edge"
                  className="Histogram-axisLabelY"
                  writingMode="tb"
                  textAnchor="middle"
                  transform={`rotate(180 0 ${plotSize.height / 2})`}
                >
                  {graphConf.Y_AXIS_LABEL}
                </text>
              </g>
            </svg>
          </div>
        </div>
        <div className="Histogram-legend">
          <ListSelect
            value={measurementState}
            onChange={setMeasurementState}
            options={measureOptions}
            style={{ flexBasis: '50%' }}
          />
          <span className="Histogram-basisDescription">{basisDescription}</span>
        </div>
      </div>
    </>
  );
}
